// ═══════════════════════════════════════════════════════════════════════════════
// Family members list (employer + helper can view; employer manages)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  isPartialFamilyMemberBirthday,
  LANGUAGE,
  USER_ROLE,
  type FamilyMemberListItem,
  type FamilyMemberTranslatedFields,
  type Language,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../components/ui/ToastProvider';
import { familyMemberApi } from '../../lib/api';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { FamilyMembersTitleIcon } from '../../components/family/FamilyMembersTitleIcon';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { AutoTranslationHint } from '../../components/translation';
import {
  buildFamilyMemberBirthdaySummary,
  IconAlertTriangle,
  IconCalendar,
  IconDocument,
  IconHash,
  IconUtensils,
  IconPill,
  splitHashtagInput,
} from './familyMemberUi';

const LIST_CONSENT_STORAGE_KEY = 'fdh-family-list-consent-v1';

type MemberDetailRow = {
  key: string;
  icon: ReactNode;
  label: string;
  content: ReactNode;
};

const memberDetailRowDividerClass =
  'divide-y divide-sky-200/90 dark:divide-sky-800/50';

const memberDetailRowShellClass = 'flex gap-3 py-3 first:pt-0 last:pb-0';

const memberDetailRowLabelClass = 'text-xs text-gray-500 dark:text-gray-400';

const memberDetailRowValueClass =
  'whitespace-pre-wrap text-sm font-semibold text-gray-900 dark:text-gray-100';

const translationLanguageNameKey = (language: Language): string => {
  if (language === LANGUAGE.ZH_HANT) return 'translation.lang.zhHant';
  if (language === LANGUAGE.ZH_HANS) return 'translation.lang.zhHans';
  if (language === LANGUAGE.EN) return 'translation.lang.en';
  if (language === LANGUAGE.ID) return 'translation.lang.id';
  if (language === LANGUAGE.FIL) return 'translation.lang.fil';
  return 'translation.lang.en';
};

const resolveDisplayText = (
  original: string,
  translated: string | undefined,
  showOriginal: boolean,
): string => {
  const orig = (original ?? '').trim();
  if (showOriginal || !translated?.trim()) {
    return orig;
  }
  return translated.trim();
};

const buildMemberDetailRows = (
  member: FamilyMemberListItem,
  t: (key: string) => string,
  birthdayRowLabel: string,
  ageSummary: string,
  tagPieces: string[],
  translatedFields?: FamilyMemberTranslatedFields,
  showOriginal = false,
): MemberDetailRow[] => {
  const rows: MemberDetailRow[] = [
    {
      key: 'birthday',
      icon: <IconCalendar />,
      label: birthdayRowLabel,
      content: <p className={memberDetailRowValueClass}>{ageSummary}</p>,
    },
  ];

  const appendTextRow = (key: string, icon: ReactNode, label: string, value: string | null | undefined) => {
    const trimmed = (value ?? '').trim();
    if (!trimmed) return;
    rows.push({
      key,
      icon,
      label,
      content: <p className={memberDetailRowValueClass}>{trimmed}</p>,
    });
  };

  appendTextRow(
    'remark',
    <IconDocument />,
    t('family.field.remark'),
    resolveDisplayText(member.remark, translatedFields?.remark, showOriginal),
  );
  appendTextRow(
    'foodPreference',
    <IconUtensils />,
    t('family.field.foodPreference'),
    resolveDisplayText(member.foodPreference, translatedFields?.foodPreference, showOriginal),
  );
  appendTextRow(
    'foodAllergy',
    <IconAlertTriangle />,
    t('family.field.foodAllergy'),
    resolveDisplayText(member.foodAllergy, translatedFields?.foodAllergy, showOriginal),
  );
  appendTextRow(
    'medicalCondition',
    <IconPill />,
    t('family.field.medicalCondition'),
    resolveDisplayText(member.medicalCondition, translatedFields?.medicalCondition, showOriginal),
  );

  if (tagPieces.length > 0) {
    rows.push({
      key: 'hashtags',
      icon: <IconHash />,
      label: t('family.field.hashtags'),
      content: (
        <ul className="mt-1 flex flex-wrap gap-2" aria-label={t('family.field.hashtags')}>
          {tagPieces.map((tag) => (
            <li key={tag}>
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100">
                {tag}
              </span>
            </li>
          ))}
        </ul>
      ),
    });
  }

  return rows;
};

const PencilIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export const FamilyMembersPage = () => {
  const { t, language } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isEmployer = user?.role === USER_ROLE.EMPLOYER;
  const isHelper = user?.role === USER_ROLE.HELPER;

  const [members, setMembers] = useState<FamilyMemberListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listConsent, setListConsent] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [showOriginalByMemberId, setShowOriginalByMemberId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setListConsent(window.localStorage.getItem(LIST_CONSENT_STORAGE_KEY) === '1');
  }, []);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await familyMemberApi.list(language);
      if (res.success && res.data) {
        const raw = res.data;
        setMembers(Array.isArray(raw) ? raw : []);
      } else {
        setError(res.error ?? t('family.loadError'));
      }
    } catch {
      setError(t('family.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, language]);

  useEffect(() => {
    setShowOriginalByMemberId({});
  }, [language]);

  useEffect(() => {
    const canFetch = !isEmployer || listConsent;
    if (!canFetch) {
      setLoading(false);
      return;
    }
    void loadMembers();
  }, [isEmployer, listConsent, loadMembers, language]);

  const handleListConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setListConsent(checked);
    if (typeof window !== 'undefined') {
      if (checked) {
        window.localStorage.setItem(LIST_CONSENT_STORAGE_KEY, '1');
      } else {
        window.localStorage.removeItem(LIST_CONSENT_STORAGE_KEY);
      }
    }
  };

  useEffect(() => {
    if (isEmployer && !listConsent) {
      setMembers([]);
    }
  }, [isEmployer, listConsent]);

  const handleAddMember = () => {
    navigate('/family-members/new');
  };

  const handleEditMember = (id: string) => {
    navigate(`/family-members/${id}/edit`);
  };

  const handleToggleShowOriginal = (memberId: string) => {
    setShowOriginalByMemberId((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  const handleConfirmDeleteMember = async () => {
    if (!deleteTargetId) return;
    const id = deleteTargetId;
    setDeleteTargetId(null);
    try {
      const res = await familyMemberApi.delete(id);
      if (res.success) {
        showSuccessToast(t('toast.familyMember.deleteSuccess'));
        await loadMembers();
      } else {
        window.alert(res.error ?? t('family.deleteError'));
      }
    } catch {
      window.alert(t('family.deleteError'));
    }
  };

  const showContent = !isEmployer || listConsent;

  return (
    <div className="min-h-full bg-[#f7f8fb] px-4 py-6 pb-24 dark:bg-gray-950">
      <div className="mx-auto max-w-lg">
        <AppPageTitleSection
          title={t('family.title')}
          subtitle={t('family.subtitle')}
          endDecoration={<FamilyMembersTitleIcon />}
        />

      <main className="relative z-10">
        {isEmployer ? (
          <section
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-800 dark:bg-amber-950/60"
            aria-labelledby="family-list-consent-heading"
          >
            <h2 id="family-list-consent-heading" className="sr-only">
              {t('family.listConsentLabel')}
            </h2>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={listConsent}
                onChange={handleListConsentChange}
                tabIndex={0}
                className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                aria-label={t('family.listConsentLabel')}
              />
              <span className="text-sm text-gray-800 dark:text-amber-100">{t('family.listConsentLabel')}</span>
            </label>
            {!listConsent ? (
              <p className="mt-2 text-sm text-amber-900 dark:text-amber-200" role="status">
                {t('family.listConsentHint')}
              </p>
            ) : null}
          </section>
        ) : null}

        {!showContent ? null : loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center dark:bg-red-950/60">
            <p className="text-red-600 dark:text-red-300" role="alert">
              {error}
            </p>
            <button
              type="button"
              onClick={() => void loadMembers()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none dark:text-red-200"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : members.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <p className="text-gray-600 dark:text-gray-300">{t('family.empty')}</p>
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
              {isEmployer ? t('family.emptyHint') : t('family.emptyHintHelper')}
            </p>
          </div>
        ) : (
          <ul className="space-y-4" role="list">
            {members.map((m) => {
              const showOriginal = showOriginalByMemberId[m.id] ?? false;
              const translatedFields = m.translation?.fields;
              const hasTranslation = Boolean(isHelper && m.translation?.hasTranslation);
              const tagSource = resolveDisplayText(
                m.hashtags ?? '',
                translatedFields?.hashtags,
                showOriginal,
              );
              const tagPieces = splitHashtagInput(tagSource);
              const birthdayRaw = (m.birthday ?? '').trim();
              const ageSummary = buildFamilyMemberBirthdaySummary(birthdayRaw, t);
              const birthdayRowLabel = isPartialFamilyMemberBirthday(birthdayRaw)
                ? t('family.field.birthday')
                : t('family.field.age');
              const nick = (m.nickname ?? '').trim();
              const displayNameAria = nick ? `${m.name} (${nick})` : m.name;
              const detailRows = buildMemberDetailRows(
                m,
                t,
                birthdayRowLabel,
                ageSummary,
                tagPieces,
                translatedFields,
                showOriginal,
              );
              const targetLanguageLabel = m.translation?.targetLanguage
                ? t(translationLanguageNameKey(m.translation.targetLanguage))
                : '';

              return (
                <li key={m.id}>
                  <article
                    className="relative rounded-2xl border border-gray-300/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/20"
                    aria-label={displayNameAria}
                  >
                    {hasTranslation ? (
                      <div className="absolute right-4 top-4 z-10">
                        <AutoTranslationHint
                          targetLanguageLabel={targetLanguageLabel}
                          showingOriginal={showOriginal}
                          onToggleOriginal={() => handleToggleShowOriginal(m.id)}
                        />
                      </div>
                    ) : null}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3
                          className={`text-lg font-bold text-gray-900 dark:text-gray-50${hasTranslation ? ' pr-10' : ''}`}
                        >
                          <span>{m.name}</span>
                          {nick ? (
                            <span className="font-normal text-gray-500 dark:text-gray-400">
                              {' '}
                              ({nick})
                            </span>
                          ) : null}
                        </h3>
                        <div className="mt-3 rounded-xl border border-sky-100 bg-sky-50/90 p-3 dark:border-sky-900/50 dark:bg-sky-950/35">
                          <div className={memberDetailRowDividerClass}>
                            {detailRows.map((row) => (
                              <div key={row.key} className={memberDetailRowShellClass}>
                                <span className="mt-0.5 shrink-0" aria-hidden>
                                  {row.icon}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className={memberDetailRowLabelClass}>{row.label}</p>
                                  {row.content}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      {isEmployer ? (
                        <div className="flex shrink-0 flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditMember(m.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                            aria-label={t('family.editMemberAria')}
                          >
                            <PencilIcon />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteTargetId(m.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-400 dark:border-rose-900/60 dark:bg-rose-950/50 dark:text-rose-100 dark:hover:bg-rose-950/80"
                            aria-label={t('family.deleteMember')}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <ConfirmDialog
        open={Boolean(deleteTargetId)}
        title={t('family.deleteMember')}
        description={t('family.deleteConfirm')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmDeleteMember()}
        onCancel={() => setDeleteTargetId(null)}
        variant="danger"
      />

      {isEmployer && listConsent ? (
        <PagePrimaryFab onClick={handleAddMember} ariaLabel={t('family.addMemberAria')}>
          <span className="text-lg font-bold leading-none" aria-hidden>
            +
          </span>
          <span>{t('family.addMember')}</span>
        </PagePrimaryFab>
      ) : null}
      </div>
    </div>
  );
};
